import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { toast } from "@/hooks/use-toast";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Watchlist {
  id: string;
  user_id: string;
  label: string;
  cattle_type: string;
  weight_class: string;
  region: string;
  states: string[] | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWatchlistInput {
  label: string;
  cattle_type: string;
  weight_class: string;
  region: string;
  states?: string[] | null;
  is_primary?: boolean;
}

export interface UpdateWatchlistInput {
  id: string;
  label?: string;
  cattle_type?: string;
  weight_class?: string;
  region?: string;
  states?: string[] | null;
  is_primary?: boolean;
}

// Weight class labels for display
export const WATCHLIST_WEIGHT_LABELS: Record<string, string> = {
  wt_300_399: "300-399 lb",
  wt_400_499: "400-499 lb",
  wt_500_599: "500-599 lb",
  wt_600_699: "600-699 lb",
  wt_700_799: "700-799 lb",
  wt_800_899: "800-899 lb",
  wt_900_999: "900-999 lb",
  wt_1000_1099: "1,000-1,099 lb",
};

const QUERY_KEY = "user-watchlists";

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** Fetch all watchlists for the current user */
export function useWatchlists() {
  const { session } = useUserProfile();
  return useQuery<Watchlist[]>({
    queryKey: [QUERY_KEY],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_watchlists")
        .select("*")
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Watchlist[];
    },
    staleTime: 60_000,
  });
}

/** Get only the primary watchlist (convenience) */
export function usePrimaryWatchlist() {
  const { data: all, ...rest } = useWatchlists();
  const primary = all?.find((w) => w.is_primary) ?? null;
  return { data: primary, ...rest };
}

/** Create a new watchlist */
export function useCreateWatchlist() {
  const qc = useQueryClient();
  const { session } = useUserProfile();

  return useMutation({
    mutationFn: async (input: CreateWatchlistInput) => {
      if (!session) throw new Error("Not authenticated");
      const userId = session.user.id;

      // If this is being set as primary, clear any existing primary first
      if (input.is_primary) {
        await supabase
          .from("user_watchlists")
          .update({ is_primary: false, updated_at: new Date().toISOString() })
          .eq("user_id", userId)
          .eq("is_primary", true);
      }

      const { data, error } = await supabase
        .from("user_watchlists")
        .insert({
          user_id: userId,
          label: input.label,
          cattle_type: input.cattle_type,
          weight_class: input.weight_class,
          region: input.region,
          states: input.states ?? null,
          is_primary: input.is_primary ?? false,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Watchlist;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ title: "Watchlist saved" });
    },
    onError: (err) => {
      toast({
        title: "Couldn't save watchlist",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    },
  });
}

/** Update an existing watchlist */
export function useUpdateWatchlist() {
  const qc = useQueryClient();
  const { session } = useUserProfile();

  return useMutation({
    mutationFn: async (input: UpdateWatchlistInput) => {
      if (!session) throw new Error("Not authenticated");
      const { id, ...fields } = input;

      // If promoting to primary, demote any existing primary first
      if (fields.is_primary) {
        await supabase
          .from("user_watchlists")
          .update({ is_primary: false, updated_at: new Date().toISOString() })
          .eq("user_id", session.user.id)
          .eq("is_primary", true);
      }

      const { error } = await supabase
        .from("user_watchlists")
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/** Delete a watchlist */
export function useDeleteWatchlist() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("user_watchlists")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ title: "Watchlist removed" });
    },
  });
}
